import {
  Box,
  Button,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  StepperProps,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { cloneDeep, isEqual } from "lodash";
import { FC, MutableRefObject, useContext, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import {
  AppContextType,
  ListViewContext,
  ListViewStateContextType,
  ListViewsContextType,
  SectionStateContextType,
  StateContext,
  StateContextType,
} from "../../context/ApplicationContext";
import { funcExecutor } from "../../functions/generic-functions";
import { useAppSelector } from "../../store/hooks";
import store from "../../store/store";
import { submitForm } from "../../store/submit-form-slice";
import { validateRequiredFields } from "../model/field-validation";
import {
  ExtendedEvents,
  FunctionDefinition,
  NameValueTuple,
  SectionConfiguration,
} from "../model/form-configuration";
import { transformFormData } from "../model/form-submit";
import red from "@mui/material/colors/red";

export type DisableStepState = { [id: number]: boolean };

let fontColor: string = "text.secondary";

const stepperStyle: SxProps = {
  ".MuiStepIcon-text": {
    display: "none",
  },
};

let steps: Array<SectionConfiguration>;
const bgOldColor: string = "#f8ffeb";
const bgColor: string = "#ffffff";

const PPStepper: FC<{
  sections: Array<SectionConfiguration>;
  setDialogState: React.Dispatch<React.SetStateAction<boolean>>;
  children: JSX.Element[];
}> = ({ sections, setDialogState, children }) => {
  const { handleSubmit, reset, getValues, setValue } = useFormContext();
  const lstContext = useContext(ListViewContext);
  const stateCtx: StateContextType = useContext(StateContext);
  const [queryParams] = useSearchParams();
  const { form } = useAppSelector((state) => state.form);
  const [activeStep, setActiveStep] = useState(0);

  const onSubmit = (data: any) => {
    setDialogState(true);
    store.dispatch(
      submitForm(
        queryParams,
        transformFormData(data, form, queryParams, lstContext),
        reset
      )
    );
  };
  steps = sections;

  const [disabledStepsState, setDisabledStepsState] =
    useState<DisableStepState>(() => {
      let newDisableStepsState: DisableStepState = {};
      steps.forEach(
        (_: SectionConfiguration, index: number) =>
          (newDisableStepsState[index] = false)
      );
      return newDisableStepsState;
    });

  const isValidTransition = (
    transitionStep: number,
    currentStep: number,
    appContext: AppContextType
  ): boolean => {
    const sectionState: SectionStateContextType = appContext.sectionState;
    return (
      isPreviousStepHidden(transitionStep, appContext) ||
      isPreviousStepComplete(transitionStep, appContext) ||
      isSecondStepTransition(transitionStep, currentStep) ||
      isSingleStepTransition(transitionStep, currentStep) ||
      isTransitionStepComplete(sectionState, transitionStep)
    );
  };

  const totalSteps = () => {
    return steps.length;
  };

  const allStepsCompleted = (): boolean => {
    const values: boolean[] = Object.keys(
      stateCtx.applicationContext.sectionState.completeState
    ).map(
      (prop) => stateCtx.applicationContext.sectionState.completeState[prop]
    );

    return values.filter((value) => !value).length == 0;
  };

  const isFirstStep = () => {
    return activeStep === 0;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const isStepHidden = (
    transitionStep: number,
    appContext: AppContextType
  ): boolean => {
    return appContext.sectionState.hideState[
      appContext.sectionState.stepToSection[transitionStep]
    ];
  };

  const isPreviousStepHidden = (
    transitionStep: number,
    appContext: AppContextType
  ): boolean => {
    if (transitionStep == 0) return false;
    return isStepHidden(transitionStep - 1, appContext);
  };

  const isStepComplete = (
    transitionStep: number,
    appContext: AppContextType
  ): boolean => {
    return appContext.sectionState.completeState[
      appContext.sectionState.stepToSection[transitionStep]
    ];
  };

  const isPreviousStepComplete = (
    transitionStep: number,
    appContext: AppContextType
  ): boolean => {
    return isStepComplete(transitionStep - 1, appContext);
  };

  const isSecondStepTransition = (
    transitionStep: number,
    currentStep: number
  ): boolean => {
    return currentStep == 0 && transitionStep == 1;
  };

  const isSingleStepTransition = (
    transitionStep: number,
    currentStep: number
  ): boolean => {
    return Math.abs(transitionStep - currentStep) == 1;
  };

  const isTransitionStepComplete = (
    sectionState: SectionStateContextType,
    transitionStep: number
  ): boolean => {
    return sectionState.completeState[
      sectionState.stepToSection[transitionStep]
    ];
  };

  const getString = (nameValue: NameValueTuple): string => nameValue.value;

  const getExpression = (nameValue: NameValueTuple): string => nameValue.value;

  const getStringArray = (nameValue: NameValueTuple): string[] => {
    const stringArrRegEx: RegExp = /\[(.*?)\]/;
    const stringVals: RegExpMatchArray | null =
      nameValue.value.match(stringArrRegEx);
    if (stringVals !== null) {
      return stringVals[1].split(",");
    } else return [] as string[];
  };

  const getParameter = (nameValue: NameValueTuple): any => {
    let param: any = undefined;
    switch (nameValue.type) {
      case "string":
        param = getString(nameValue);
        break;
      case "stringArray":
        param = getStringArray(nameValue);
        break;
      case "rpn":
        param = getExpression(nameValue);
        break;
      default:
        param = nameValue.value;
    }
    return param;
  };

  const onBlurHandler = (
    appContext: AppContextType,
    listsContext: ListViewsContextType
  ): void => {
    const extendedEvents: ExtendedEvents | undefined =
      form.sections[activeStep].extendedEvents;
    extendedEvents?.onBlur
      ?.slice()
      .sort((a: FunctionDefinition, b: FunctionDefinition) => a.order - b.order)
      .forEach((func: FunctionDefinition) => {
        const sortedParams: NameValueTuple[] = func.parameters
          .slice()
          .sort((a: NameValueTuple, b: NameValueTuple) => a.order - b.order);

        funcExecutor(
          func.name,
          sortedParams.map((nameValue: NameValueTuple): any =>
            getParameter(nameValue)
          ),
          getValues,
          setValue,
          appContext,
          undefined,
          listsContext
        );
      });
  };

  const handleStep = (currentStep: number, transitionStep: number) => () => {
    const newAppContext: AppContextType = cloneDeep(
      stateCtx.applicationContext
    );
    const newListsContext: ListViewsContextType = cloneDeep(
      lstContext.listViewsState
    );

    // This is something we need to refactor at some point
    // We need to call the onBlurHandler right away so that
    // we can set the number of minimumRows on the context
    // before we call validateRequired fields which also controls
    // the validation of minimum number of rows for listviews.
    // technically we want to develop a pattern where the last thing
    // we do is to set both the list and application contexts.
    // onBlurHandler(newAppContext, newListsContext);
    // if (!isEqual(lstContext.listViewsState, newListsContext)) {
    //   lstContext.setListViewsState(newListsContext);
    // }

    const areAllFieldsValid: boolean = validateRequiredFields(
      sections[activeStep],
      getValues(),
      newAppContext,
      newListsContext
    );

    const isAValidStepperTransition: boolean = isValidTransition(
      transitionStep,
      activeStep,
      newAppContext
    );
    if (areAllFieldsValid && isAValidStepperTransition) {
      const newCompleted = newAppContext.sectionState.completeState;
      newCompleted[newAppContext.sectionState.stepToSection[activeStep]] = true;
      newAppContext.sectionState.completeState = newCompleted;
      setActiveStep(transitionStep);
    }

    //if (!isEqual(newAppContext, stateCtx.applicationContext)) {
    //  stateCtx.setApplicationContext(newAppContext);
    //}

    onBlurHandler(newAppContext, newListsContext);
    if (!isEqual(newAppContext, stateCtx.applicationContext)) {
      stateCtx.setApplicationContext(newAppContext);
    }
    if (!isEqual(lstContext.listViewsState, newListsContext)) {
      lstContext.setListViewsState(newListsContext);
    }
  };

  const findNonHiddenStep = (
    backSearch: boolean,
    appContext: AppContextType
  ): number | undefined => {
    let ret: number | undefined = undefined;
    if (backSearch) {
      ret = steps
        .slice(0, activeStep)
        .slice()
        .reverse()
        .map((step, index) =>
          !appContext.sectionState.hideState[step.id]
            ? activeStep - (index + 1)
            : -1
        )
        .filter((stepNum) => stepNum != -1)[0];
    } else {
      ret = steps
        .slice(activeStep + 1)
        .map((step, index) =>
          !appContext.sectionState.hideState[step.id]
            ? activeStep + (index + 1)
            : -1
        )
        .filter((stepNum) => stepNum != -1)[0];
    }

    return ret;
  };

  const handleNext = () => {
    const newAppContext: AppContextType = cloneDeep(
      stateCtx.applicationContext
    );

    const newListsContext: ListViewsContextType = cloneDeep(
      lstContext.listViewsState
    );

    const newActiveStep: number = isLastStep()
      ? activeStep
      : findNonHiddenStep(false, newAppContext) ?? activeStep;

    const isValidRequiredFields = validateRequiredFields(
      sections[activeStep],
      getValues(),
      newAppContext,
      newListsContext
    );

    const isValidTransitionState = isValidTransition(
      newActiveStep,
      activeStep,
      stateCtx.applicationContext
    );

    if (isValidRequiredFields && isValidTransitionState) {
      const newCompleted = cloneDeep(
        stateCtx.applicationContext.sectionState.completeState
      );
      newCompleted[
        stateCtx.applicationContext.sectionState.stepToSection[activeStep]
      ] = true;
      newAppContext.sectionState.completeState = newCompleted;
      setActiveStep(newActiveStep);
    }
    onBlurHandler(newAppContext, newListsContext);
    if (!isEqual(newAppContext, stateCtx.applicationContext)) {
      stateCtx.setApplicationContext(newAppContext);
    }
    if (!isEqual(lstContext.listViewsState, newListsContext)) {
      lstContext.setListViewsState(newListsContext);
    }
    stepRefs
      .filter((stepRef) => stepRef.stepIndex == newActiveStep)[0]
      ?.ref.current?.focus();
  };

  const handleBack = () => {
    const newAppContext: AppContextType = cloneDeep(
      stateCtx.applicationContext
    );
    const newListsContext: ListViewsContextType = cloneDeep(
      lstContext.listViewsState
    );
    const transitionStep: number =
      activeStep > 0
        ? findNonHiddenStep(true, newAppContext) ?? activeStep
        : activeStep;
    if (
      validateRequiredFields(
        sections[activeStep],
        getValues(),
        newAppContext,
        newListsContext
      ) &&
      isValidTransition(transitionStep, activeStep, stateCtx.applicationContext)
    ) {
      const newCompleted = cloneDeep(
        stateCtx.applicationContext.sectionState.completeState
      );
      newCompleted[
        stateCtx.applicationContext.sectionState.stepToSection[activeStep]
      ] = true;
      newAppContext.sectionState.completeState = newCompleted;
      setActiveStep(transitionStep);
    }
    onBlurHandler(newAppContext, newListsContext);
    if (!isEqual(newAppContext, stateCtx.applicationContext)) {
      stateCtx.setApplicationContext(newAppContext);
    }
    if (!isEqual(lstContext.listViewsState, newListsContext)) {
      lstContext.setListViewsState(newListsContext);
    }
    stepRefs
      .filter((stepRef) => stepRef.stepIndex == transitionStep)[0]
      ?.ref.current?.focus();
  };

  let theme: Theme = useTheme();
  let isMobileBreakpoint: boolean = useMediaQuery(theme.breakpoints.down("md"));
  let orientation: StepperProps["orientation"] = isMobileBreakpoint
    ? "vertical"
    : "horizontal";
  const displayNone: SxProps = { display: "none" };

  //creates an array of Refs used to control the focus on the Step when Back/Next buttons are clicked.
  type StepRefsType = {
    stepIndex: number;
    ref: MutableRefObject<HTMLButtonElement> | MutableRefObject<null>;
  };
  const stepRefs = steps.map((section, index) => {
    return { stepIndex: index, ref: useRef(null) } as StepRefsType;
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ width: "100%" }}>
        <Stepper orientation={orientation} nonLinear activeStep={activeStep}>
          {steps.map((section, index) => {
            return (
              <Step
                sx={
                  stateCtx.applicationContext.sectionState.hideState[section.id]
                    ? displayNone
                    : {}
                }
                disabled={disabledStepsState[index]}
                key={section.id}
                completed={
                  stateCtx.applicationContext.sectionState.completeState[
                    section.id
                  ]
                }
              >
                <StepButton
                  ref={
                    stepRefs.filter((stepRef) => stepRef.stepIndex == index)[0]
                      ?.ref
                  }
                  color="inherit"
                  onClick={handleStep(activeStep, index)}
                >
                  <StepLabel error={false} sx={stepperStyle}>
                    {section.label}
                  </StepLabel>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>
        <div>
          {children[activeStep]}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button
              sx={isFirstStep() ? { display: "none" } : { mr: 1 }}
              color="inherit"
              disabled={
                isFirstStep() ||
                !lstContext.listViewsState[steps[activeStep].id]?.expanded
              }
              onClick={handleBack}
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            <Button
              onClick={handleNext}
              sx={isLastStep() ? { display: "none" } : { mr: 1 }}
              disabled={
                isLastStep() ||
                !lstContext.listViewsState[steps[activeStep].id]?.expanded
              }
            >
              Next
            </Button>
          </Box>
        </div>
      </Box>
    </form>
  );
};

export default PPStepper;
